import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Download, Send, Edit } from "lucide-react";
import { usePDF } from "react-to-pdf";
import { InquiryItem, InquiryDriverItem } from "@/types/database";

export function InquiryDriverView() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch original inquiry data
  const { data: inquiry, isLoading: isInquiryLoading } = useQuery<InquiryItem>({
    queryKey: ["inquiry", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inquiry")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as InquiryItem;
    },
  });

  // Fetch driver-specific data if it exists
  const { data: driverData, isLoading: isDriverDataLoading } = useQuery<InquiryDriverItem | null>({
    queryKey: ["inquiry_driver", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inquiry_driver")
        .select("*")
        .eq("inquiry_id", id)
        .maybeSingle();

      if (error) throw error;
      return data as InquiryDriverItem | null;
    },
  });

  const formatIDR = (value: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const { toPDF, targetRef } = usePDF({
    filename: inquiry ? `${inquiry.name || id}-driver.pdf` : `driver-ebt.pdf`,
    page: { margin: 20 }
  });

  const cleanTitle = (title: string) => {
    if (!title) return title;
    // Remove only transport cost information, keep the area
    return title
      .replace(/ - Transport: (Rp )?[\d.,]+/g, '')  // Format: " - Transport: Rp 135000" or " - Transport: 135000"
      .replace(/ - Transport Cost: [\d.,]+/g, '');   // Format: " - Transport Cost: 135000"
  };

  if (isInquiryLoading || isDriverDataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!inquiry) return null;

  // Determine which data to use for display
  // For transport costs and parking, use driver data if available
  // For everything else, use the original inquiry data
  const displayData = {
    ...inquiry.tour_package_json,
  };

  // If driver data exists, override transport_cost and parking in each day
  if (driverData && driverData.tour_package_json) {
    Object.entries(driverData.tour_package_json).forEach(([day, dayData]) => {
      if (displayData[day]) {
        displayData[day] = {
          ...displayData[day],
          transport_cost: dayData.transport_cost,
          parking: dayData.parking,
          entrance_adult: dayData.entrance_adult || displayData[day].entrance_adult,
          entrance_child: dayData.entrance_child || displayData[day].entrance_child,
          meal_cost: dayData.meal_cost,  // Use driver's meal cost
          other_cost: dayData.other_cost,  // Use driver's other cost
        };
      }
    });
  }

  // Calculate totals on the fly from the displayData
  const totalTransportCost = Object.values(displayData).reduce(
    (sum: number, day: any) => sum + (Number(day.transport_cost) || 0),
    0
  );

  const totalParkingCost = Object.values(displayData).reduce((sum: number, day: any) => {
    const parkingCosts = Object.values(day.parking || {}).reduce(
      (parkingSum: number, cost: any) => parkingSum + (Number(cost) || 0),
      0
    );
    return sum + Number(parkingCosts);
  }, 0);

  const totalEntranceAdultCost = Object.values(displayData).reduce((sum: number, day: any) => {
    const entranceAdultCosts = day.tours?.reduce((entranceSum: number, tour: string) => {
      return entranceSum + ((day.entrance_adult?.[tour] || 0) * inquiry.adult_count);
    }, 0) || 0;
    return sum + entranceAdultCosts;
  }, 0);

  const totalEntranceChildCost = Object.values(displayData).reduce((sum: number, day: any) => {
    const entranceChildCosts = day.tours?.reduce((entranceSum: number, tour: string) => {
      return entranceSum + ((day.entrance_child?.[tour] || 0) * inquiry.child_count);
    }, 0) || 0;
    return sum + entranceChildCosts;
  }, 0);

  const totalMealCost = Object.values(displayData).reduce(
    (sum: number, day: any) => sum + (Number(day.meal_cost) || 0),
    0
  );

  const totalOtherCost = Object.values(displayData).reduce(
    (sum: number, day: any) => sum + (Number(day.other_cost) || 0),
    0
  );

  const grandTotal = totalTransportCost + totalParkingCost + totalEntranceAdultCost + 
                     totalEntranceChildCost + totalMealCost + totalOtherCost;

  const handleDownloadPDF = () => {
    toPDF();
  };

  return (
    <div>
      <div id="inquiry-content" ref={targetRef} className="container mx-auto py-2 space-y-2 max-w-[210mm]">
        <Card className="shadow-sm p-4 bg-primary/5">
          <CardContent className="p-0 flex justify-center">
            <h2 className="text-3xl font-bold text-primary">Voucher Code: {inquiry.voucher_code}</h2>
          </CardContent>
        </Card>

        {/* Driver Name */}
        {driverData?.driver_name && (
          <Card className="shadow-sm p-1">
            <CardHeader className="p-1">
              <CardTitle className="text-sm font-medium">Driver Information</CardTitle>
            </CardHeader>
            <CardContent className="p-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p>{driverData.driver_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Guest Information */}
        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Guest Information</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <Table className="border-collapse border border-gray-800">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Name</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Guest</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Days</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Hotel</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Car Type</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Date</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Flight Information</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.name}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">
                    {inquiry.adult_count} Adult & {inquiry.child_count} Child
                  </TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.nights}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.hotel_type}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.car_type || "-"}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{format(new Date(inquiry.start_date), "dd MMM yyyy")}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 whitespace-pre-wrap">{inquiry.flight_information || '-'}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 whitespace-pre-wrap">{inquiry.remarks || '-'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Tour Package Information */}
        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Tour Package Information</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <div className="space-y-2">
              {Object.entries(displayData).map(([day, data]) => (
                <div key={day} className="p-2 border border-gray-800 rounded-md">
                  <h3 className="text-xs font-medium mb-2">
                    {(() => {
                      // Get the original title
                      const originalTitle = (data as any).title || (data as any).captured_tours?.[day]?.title;
                      
                      if (originalTitle) {
                        return cleanTitle(originalTitle);
                      }
                      
                      // Fallback if no title exists
                      return `Day ${day.replace('day_', '')} - ${(data as any).tour_package_name || ''}`;
                    })()}
                  </h3>
                  <div className="">
                    <Table className="border-collapse border border-gray-800">
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Tours</TableHead>
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Entrance/Adult</TableHead>
                          {inquiry.child_count > 0 && (
                            <TableHead className="h-4 p-2 text-xs border border-gray-800">Entrance/Child</TableHead>
                          )}
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Parking</TableHead>
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Transport</TableHead>
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Meal Cost</TableHead>
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Other Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.tours.map((tour, index) => (
                          <TableRow key={index} className="hover:bg-transparent">
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">{tour}</TableCell>
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">{formatIDR(data.entrance_adult[tour] || 0)}</TableCell>
                            {inquiry.child_count > 0 && (
                              <TableCell className="h-4 p-2 text-xs border border-gray-800">{formatIDR(data.entrance_child[tour] || 0)}</TableCell>
                            )}
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">
                              {index === 0 ? formatIDR(Object.values(data.parking || {}).reduce((sum: number, cost: any) => sum + (Number(cost) || 0), 0)) : ''}
                            </TableCell>
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">
                              {index === 0 ? formatIDR(data.transport_cost) : ''}
                            </TableCell>
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">
                              {index === 0 ? formatIDR(data.meal_cost) : ''}
                            </TableCell>
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">
                              {index === 0 ? formatIDR(data.other_cost) : ''}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Day Notes */}
                  {data.note && (
                    <div className="mt-4">
                      <h4 className="text-xs font-semibold mb-1">Notes:</h4>
                      <p className="text-xs whitespace-pre-line pl-2 border-l-2 border-primary">{String(data.note)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Hotel Information */}
        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Hotel Information</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <Table className="border-collapse border border-gray-800">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Hotel Name</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Check-in</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Check-out</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Room Count</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Nights</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(inquiry.hotel_json?.hotels || {}).map(([day, hotel]) => (
                  <TableRow key={day} className="hover:bg-transparent">
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{hotel.hotel_name}</TableCell>
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{format(new Date(hotel.checkin_date), "dd MMM yyyy")}</TableCell>
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{format(new Date(hotel.checkout_date), "dd MMM yyyy")}</TableCell>
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{hotel.room_count}</TableCell>
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{hotel.nights}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Grand Total Summary */}
        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Grand Total Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <Table className="border-collapse border border-gray-800">
              <TableBody>
                {/* Transport Cost */}
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs font-medium border border-gray-800">Transport Cost</TableCell>
                  <TableCell className="h-4 p-2 text-xs text-right border border-gray-800">
                    {formatIDR(totalTransportCost)}
                  </TableCell>
                </TableRow>

                {/* Entrance Fees */}
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">
                    Entrance Fee Adult ({inquiry.adult_count} guests)
                  </TableCell>
                  <TableCell className="h-4 p-2 text-xs text-right border border-gray-800">
                    {formatIDR(totalEntranceAdultCost)}
                  </TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">
                    Entrance Fee Child ({inquiry.child_count} guests)
                  </TableCell>
                  <TableCell className="h-4 p-2 text-xs text-right border border-gray-800">
                    {formatIDR(totalEntranceChildCost)}
                  </TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs font-medium border border-gray-800">Total Entrance Fee</TableCell>
                  <TableCell className="h-4 p-2 text-xs text-right font-medium border border-gray-800">
                    {formatIDR(totalEntranceAdultCost + totalEntranceChildCost)}
                  </TableCell>
                </TableRow>

                {/* Meal and Other Costs */}
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">Meal Cost</TableCell>
                  <TableCell className="h-4 p-2 text-xs text-right border border-gray-800">
                    {formatIDR(totalMealCost)}
                  </TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">Other Cost</TableCell>
                  <TableCell className="h-4 p-2 text-xs text-right border border-gray-800">
                    {formatIDR(totalOtherCost)}
                  </TableCell>
                </TableRow>

                {/* Parking Cost */}
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">Parking Cost</TableCell>
                  <TableCell className="h-4 p-2 text-xs text-right border border-gray-800">
                    {formatIDR(totalParkingCost)}
                  </TableCell>
                </TableRow>

                {/* Grand Total */}
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs font-medium border border-gray-800">Grand Total</TableCell>
                  <TableCell className="h-4 p-2 text-xs text-right font-medium border border-gray-800">
                    {formatIDR(grandTotal)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 mt-8 print:hidden sticky bottom-0 w-full bg-white border-t shadow-lg p-4">
        <Button 
          variant="outline" 
          onClick={handleDownloadPDF}
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
        <Button 
          onClick={() => navigate(`/inquiry-driver-edit/${id}`)}
        >
          <Edit className="w-4 h-4 mr-2" />
          Edit Transport & Parking
        </Button>
      </div>
    </div>
  );
}
